import { ReactNode, useEffect, useState } from 'react'
import { Response, showError } from '../../../../../../_metronic/helpers'
import { Pagination } from 'react-bootstrap'
import { DetailPanel, TableEmptyState } from '../../../../../components'

type HistoricoSectionProps<T> = {
  /** Nome da lista no plural, para o estado vazio ("Avaliações", "Prescrições"). */
  title: string
  fetcher: (page: number, size: number) => Promise<Response<Array<T>>>
  renderItem: (item: T) => ReactNode
}

/**
 * Lista paginada de documentos do paciente (avaliações, prescrições) em grade de cartões. O título
 * fica com quem a envolve (aba do `DocumentosPacienteCard`); aqui só a grade e a paginação.
 */
export function HistoricoSection<T>({ title, fetcher, renderItem }: HistoricoSectionProps<T>) {
  const [page, setPage] = useState(0)
  const [data, setData] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [carregando, setCarregando] = useState(true)

  const pageSize = 8
  const totalPages = Math.ceil(total / pageSize)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setCarregando(true)
        const res = await fetcher(page, pageSize)
        setData(res.data ?? [])
        setTotal(res.totalElements)
      } catch (error) {
        showError(error)
      } finally {
        setCarregando(false)
      }
    }

    fetchData()
  }, [page, fetcher])

  // Sem isso o estado vazio aparecia por um instante antes de os dados chegarem.
  if (carregando && data.length === 0) {
    return <DetailPanel isLoading loadingLabel={`Carregando ${title.toLowerCase()}…`} />
  }

  if (data.length === 0) {
    return <TableEmptyState title={`Nenhuma ${title.toLowerCase()} para este paciente`} />
  }

  return (
    <div className='d-flex flex-column'>
      <div
        className='d-grid gap-3'
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}
      >
        {data.map(renderItem)}
      </div>
      {totalPages > 1 && (
        <Pagination className='justify-content-end mt-4 mb-0'>
          <Pagination.Prev disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Pagination.Prev>

          {Array.from({ length: totalPages }).map((_, index) => (
            <Pagination.Item key={index} active={index === page} onClick={() => setPage(index)}>
              {index + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next disabled={page === totalPages - 1} onClick={() => setPage((p) => p + 1)}>
            Próximo
          </Pagination.Next>
        </Pagination>
      )}
    </div>
  )
}
